import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Chip,
  Button,
  Avatar,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
  CheckCircle as SuccessIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Assignment as TaskIcon,
  Person as PersonIcon,
  Settings as SystemIcon,
  MarkEmailRead as MarkReadIcon,
  KeyboardArrowUp as ScrollUpIcon,
} from '@mui/icons-material';
import { useNotifications } from './NotificationContext';
import { AdminNotification } from '../services/adminService';
import { formatDistanceToNow } from 'date-fns';

interface NotificationPanelProps {
  open: boolean;
  onClose: () => void;
}

const NotificationPanel: React.FC<NotificationPanelProps> = ({ open, onClose }) => {
  const { 
    notifications, 
    loading, 
    markAsRead, 
    deleteNotification,
    unreadCount 
  } = useNotifications();

  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const [showScrollTop, setShowScrollTop] = React.useState(false);

  // Auto-scroll to top when panel opens or new notifications arrive
  React.useEffect(() => {
    if (open && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [open, notifications.length]);

  // Handle scroll position for scroll-to-top button
  const handleScroll = React.useCallback(() => {
    if (scrollContainerRef.current) {
      const { scrollTop } = scrollContainerRef.current;
      setShowScrollTop(scrollTop > 200);
    }
  }, []);

  const scrollToTop = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getNotificationIcon = (type: AdminNotification['type']) => {
    const iconMap = {
      info: <InfoIcon color="info" />,
      success: <SuccessIcon color="success" />,
      warning: <WarningIcon color="warning" />,
      error: <ErrorIcon color="error" />,
      task: <TaskIcon color="primary" />,
      ninja: <PersonIcon color="secondary" />,
      system: <SystemIcon color="action" />,
    };
    return iconMap[type] || <InfoIcon />;
  };

  const getPriorityColor = (priority: AdminNotification['priority']) => {
    const colorMap = {
      low: 'default' as const,
      medium: 'primary' as const,
      high: 'warning' as const,
      critical: 'error' as const,
    };
    return colorMap[priority] || 'default';
  };

  const handleMarkAsRead = async (notificationId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    await markAsRead(notificationId);
  };

  const handleDelete = async (notificationId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    await deleteNotification(notificationId);
  };

  const handleMarkAllAsRead = async () => {
    const unreadNotifications = notifications.filter(n => !n.isRead);
    for (const notification of unreadNotifications) {
      await markAsRead(notification.id);
    }
  };

  const recentNotifications = notifications.slice(0, 50); // Limit to 50 most recent

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 400 },
          maxWidth: 400,
        },
      }}
    >
      <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" component="div">
              Notifications
            </Typography>
            {unreadCount > 0 && (
              <Chip 
                label={unreadCount} 
                size="small" 
                sx={{ 
                  bgcolor: 'error.main', 
                  color: 'error.contrastText',
                  fontWeight: 'bold',
                  minWidth: 20,
                  height: 20,
                  fontSize: '0.75rem',
                }}
              />
            )}
          </Box>
          <IconButton
            edge="end"
            color="inherit"
            onClick={onClose}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
        
        {unreadCount > 0 && (
          <Box sx={{ mt: 1 }}>
            <Button
              size="small"
              startIcon={<MarkReadIcon />}
              onClick={handleMarkAllAsRead}
              sx={{ 
                color: 'primary.contrastText',
                borderColor: 'primary.contrastText',
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
              }}
              variant="outlined"
            >
              Mark All Read
            </Button>
          </Box>
        )}
      </Box>

      <Box 
        sx={{ 
          flex: 1, 
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : recentNotifications.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Avatar sx={{ mx: 'auto', mb: 2, bgcolor: 'grey.100' }}>
              <InfoIcon color="disabled" />
            </Avatar>
            <Typography variant="body2" color="text.secondary">
              No notifications yet
            </Typography>
          </Box>
        ) : (
          <Box 
            ref={scrollContainerRef}
            onScroll={handleScroll}
            sx={{ 
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              scrollBehavior: 'smooth',
              position: 'relative',
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'rgba(0,0,0,0.1)',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(0,0,0,0.2)',
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: 'rgba(0,0,0,0.3)',
              },
            }}
          >
            <List sx={{ py: 1, px: 0 }}>
              {recentNotifications.map((notification) => (
                <ListItem
                  key={notification.id}
                  sx={{
                    bgcolor: notification.isRead ? 'transparent' : 'action.hover',
                    borderLeft: notification.isRead ? 'none' : '4px solid',
                    borderLeftColor: `${getPriorityColor(notification.priority)}.main`,
                    py: 2,
                    px: 2,
                    mb: 0.5,
                    borderRadius: 1,
                    mx: 1,
                    '&:hover': {
                      bgcolor: 'action.selected',
                      transform: 'translateX(2px)',
                      transition: 'all 0.2s ease-in-out',
                    },
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {getNotificationIcon(notification.type)}
                  </ListItemIcon>
                  
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: notification.isRead ? 400 : 600,
                            color: notification.isRead ? 'text.secondary' : 'text.primary',
                            flex: 1,
                            pr: 1,
                          }}
                        >
                          {notification.title}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                          <Chip
                            label={notification.priority}
                            size="small"
                            color={getPriorityColor(notification.priority)}
                            sx={{ 
                              height: 18,
                              fontSize: '0.7rem',
                              fontWeight: 500,
                            }}
                          />
                        </Box>
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: notification.isRead ? 'text.disabled' : 'text.secondary',
                            mb: 0.5,
                            display: '-webkit-box',
                            WebkitBoxOrient: 'vertical',
                            WebkitLineClamp: 2,
                            overflow: 'hidden',
                          }}
                        >
                          {notification.message}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                          <Typography variant="caption" color="text.disabled">
                            {formatDistanceToNow(notification.createdAt, { addSuffix: true })}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {!notification.isRead && (
                              <Tooltip title="Mark as read">
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleMarkAsRead(notification.id, e)}
                                  sx={{ p: 0.5 }}
                                >
                                  <MarkReadIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={(e) => handleDelete(notification.id, e)}
                                sx={{ p: 0.5 }}
                                color="error"
                              >
                                <DeleteIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
            
            {/* Scroll to Top Button */}
            {showScrollTop && (
              <IconButton
                onClick={scrollToTop}
                sx={{
                  position: 'sticky',
                  bottom: 16,
                  right: 16,
                  float: 'right',
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  boxShadow: 2,
                  '&:hover': {
                    bgcolor: 'primary.dark',
                    boxShadow: 4,
                  },
                  zIndex: 1000,
                }}
                size="small"
              >
                <ScrollUpIcon />
              </IconButton>
            )}
          </Box>
        )}
      </Box>

      {notifications.length > 50 && (
        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
          <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
            Showing 50 most recent notifications
          </Alert>
        </Box>
      )}
    </Drawer>
  );
};

export default NotificationPanel;
